import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import {
    FormGroupDirective,
    ReactiveFormsModule,
    UntypedFormBuilder,
    Validators,
} from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatInputModule } from '@angular/material/input'
import { Router, RouterModule } from '@angular/router'
import { ErrorType } from '@error-util-lib/error.model'
import { LandingPageSectionContainerModule } from '@landing-page/components/container/landing-page-section-container.module'
import { LandingPageRegisterFormErrorComponent } from '@landing-page/sign-up/register/register-form-error/landing-page-register-form-error.component'
import { LandingPageRegisterFormSuccessComponent } from '@landing-page/sign-up/register/register-form-success/landing-page-register-form-success.component'
import { SignUpService } from '@landing-page/sign-up/sign-up.service'
import { TranslationModule } from '@localization-lib/language/translation.module'
import { ButtonGroupModule } from '@shared-ui-lib/button-group/button-group.module'
import { ButtonModule } from '@shared-ui-lib/button/button.module'
import { LottieModule } from '@shared-ui-lib/lottie/lottie.module'
import { ExpandableDirectiveModule } from '@shared-util-lib/directives/expandable/expandable-directive.module'
import { AsteriskModule } from '@shared-util-lib/pipes/asterisk/asterisk.module'
import { MailValidationRegex } from '@util-lib/validation/mailValidationRegex'
import { PhoneNumberValidationRegex } from '@util-lib/validation/phoneNumberValidationRegex'

@Component({
    standalone: true,
    imports: [
        LottieModule,
        AsteriskModule,
        ButtonGroupModule,
        ButtonModule,
        CommonModule,
        ExpandableDirectiveModule,
        LandingPageRegisterFormErrorComponent,
        LandingPageRegisterFormSuccessComponent,
        LandingPageSectionContainerModule,
        MatCheckboxModule,
        MatInputModule,
        ReactiveFormsModule,
        RouterModule,
        TranslationModule,
    ],
    selector: 'app-landing-page-register',
    templateUrl: './landing-page-register.component.html',
    styleUrls: ['./landing-page-register.component.scss'],
})
export class LandingPageRegisterComponent {
    loading = false

    signUpSuccess = false
    signUpError = false

    @Input() title?: string
    @Input() subtitle?: string

    @Input() successTitle?: string
    @Input() successSubtitle?: string
    @Input() successShowNextSteps?: boolean

    _isDemoRequest = false
    @Input()
    set isDemoRequest(value: boolean) {
        this._isDemoRequest = coerceBooleanProperty(value)
    }

    constructor(
        private fb: UntypedFormBuilder,
        private signUpService: SignUpService,
        private router: Router
    ) {}

    formGroup = this.fb.group({
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        email: [
            '',
            [Validators.required, Validators.pattern(MailValidationRegex)],
        ],
        company: ['', [Validators.required]],
        phoneNumber: ['', [Validators.pattern(PhoneNumberValidationRegex)]],
        dataPrivacyAccepted: [false, [Validators.requiredTrue]],
    })

    handleSubmit(formDirective: FormGroupDirective) {
        this.loading = true
        this.formGroup.disable()
        this.signUpService
            .sendSignUpRequest(this.formGroup.value, this._isDemoRequest)
            .subscribe({
                next: () => {
                    this.loading = false
                    this.formGroup.enable()
                    this.formGroup.reset()
                    formDirective.resetForm()
                    this.signUpSuccess = true
                    this.signUpError = false
                },
                error: (error: ErrorType) => {
                    this.loading = false
                    this.formGroup.enable()
                    this.signUpSuccess = false
                    this.signUpError = true
                },
            })
        window.scrollTo({ left: 0, top: 0, behavior: 'smooth' })
    }

    showError(formControlName: string) {
        return (
            this.formGroup.controls[formControlName].invalid &&
            this.formGroup.controls[formControlName].touched
        )
    }

    errorMessage(formControlName: string) {
        const control = this.formGroup.controls[formControlName]
        if (control.hasError('required')) {
            return 'is required'
        } else if (control.hasError('pattern')) {
            return 'format is not valid'
        }
    }

    handleBackClick() {
        this.router.navigate(['/'])
    }

    handleRetryClick() {
        this.signUpSuccess = false
        this.signUpError = false
        window.scrollTo({ left: 0, top: 0, behavior: 'smooth' })
    }
}
